import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Button,
  Input,
  Select,
  DatePicker,
  Typography,
  Space,
  Spin,
  Alert,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  mocService,
  MocChangeType,
  MOC_CHANGE_TYPE_KEY_PERSONNEL,
  type MocHandoverItem,
  type MocHandoverReason,
} from '../../services/moc.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import {
  MocHandoverFields,
  MocHandoverImpactTable,
  handoverPayloadFromForm,
  type HandoverFormValues,
} from '@/components/moc/MocHandoverSection';

const { Title, Text } = Typography;
const { TextArea } = Input;

const MOCNewPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [types, setTypes] = useState<MocChangeType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const [preview, setPreview] = useState<{ items: MocHandoverItem[]; warnings: string[] } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const changeTypeId = Form.useWatch('change_type_id', form) as string | undefined;
  const handoverReason = Form.useWatch('handover_reason', form) as MocHandoverReason | undefined;
  const isKeyPersonnel = types.find((t) => t.id === changeTypeId)?.code === MOC_CHANGE_TYPE_KEY_PERSONNEL;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ct = await mocService.getChangeTypes();
        if (!cancelled) setTypes(ct);
      } catch {
        if (!cancelled) message.error('Could not load change types');
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      moc_date: dayjs(),
      urgency: 'routine',
      handover_reason: 'resignation',
    });
  }, [form]);

  useEffect(() => {
    if (!types.length) return;
    const code = searchParams.get('changeType');
    const type = code ? types.find((t) => t.code === code) : undefined;
    if (!type) return;
    const reason = (searchParams.get('reason') as MocHandoverReason | null) || 'resignation';
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    form.setFieldsValue({
      change_type_id: type.id,
      handover_from_user_id: searchParams.get('fromUser') || undefined,
      handover_reason: reason,
      handover_dates: from && to ? [dayjs(from), dayjs(to)] : undefined,
      change_title: reason === 'leave' ? 'Approval delegation during leave' : 'Responsibility handover',
    });
  }, [types, searchParams, form]);

  const runPreview = async () => {
    const values = form.getFieldsValue() as HandoverFormValues & { department_id?: string };
    if (!values.handover_from_user_id) {
      message.warning('Select the user handing over first');
      return;
    }
    setPreviewing(true);
    try {
      setPreview(
        await mocService.previewHandover({
          from_user_id: values.handover_from_user_id,
          to_user_id: values.handover_to_user_id,
          reason: values.handover_reason || 'resignation',
          department_id: values.department_id,
        })
      );
    } catch {
      message.error('Could not preview the handover impact');
    } finally {
      setPreviewing(false);
    }
  };

  const onFinish = async (values: Record<string, unknown>) => {
    setSubmitting(true);
    try {
      const md = values.moc_date as dayjs.Dayjs | undefined;
      const created = await mocService.create({
        moc_date: md?.format('YYYY-MM-DD') ?? dayjs().format('YYYY-MM-DD'),
        change_title: values.change_title,
        change_type_id: values.change_type_id,
        reason_for_change: values.reason_for_change,
        detailed_description: values.detailed_description,
        urgency: values.urgency || 'routine',
        department_id: values.department_id as string,
      });
      if (isKeyPersonnel) {
        try {
          const deselected = (preview?.items || []).filter((i) => !i.mandatory && !i.selected);
          await mocService.saveHandover(created.id, {
            ...handoverPayloadFromForm(values as HandoverFormValues),
            items: deselected.map((i) => ({ item_type: i.item_type, ref_id: i.ref_id, selected: false })),
          });
        } catch (e: any) {
          message.warning(e?.response?.data?.message || 'MOC saved, but the handover could not be saved — complete it on the MOC');
        }
      }
      message.success('MOC draft saved');
      navigate(`/grc/moc/${created.id}`, { replace: true });
    } catch (e: unknown) {
      console.error(e);
      message.error('Could not create MOC draft');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/grc/moc')}>
            Back to register
          </Button>
          <Title level={4} style={{ marginTop: 8, marginBottom: 0 }}>
            New MOC (draft)
          </Title>
          <Text type="secondary">Complete the required fields and save to create a draft record.</Text>
        </div>

        <Card>
          {loadingTypes ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin />
            </div>
          ) : (
            <Form form={form} layout="vertical" onFinish={(v) => void onFinish(v)} requiredMark="optional">
              <Form.Item name="moc_date" label="Date" rules={[{ required: true, message: 'Date is required' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="change_type_id"
                label="Change type"
                rules={[{ required: true, message: 'Change type is required' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={types.map((t) => ({ value: t.id, label: `${t.name} (${t.code})` }))}
                  placeholder="Select change type"
                />
              </Form.Item>
              <Form.Item
                name="change_title"
                label="Change title"
                rules={[{ required: true, message: 'Title is required' }]}
              >
                <Input maxLength={500} showCount />
              </Form.Item>
              <Form.Item
                name="department_id"
                label="Department"
                rules={[{ required: true, message: 'Department is required' }]}
              >
                <DepartmentSelect />
              </Form.Item>
              <Form.Item name="urgency" label="Urgency">
                <Select
                  options={[
                    { value: 'routine', label: 'Routine' },
                    { value: 'urgent', label: 'Urgent' },
                    { value: 'emergency', label: 'Emergency' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="reason_for_change"
                label="Reason for change"
                rules={[{ required: true, message: 'Reason is required' }]}
              >
                <TextArea rows={4} maxLength={8000} showCount />
              </Form.Item>
              <Form.Item
                name="detailed_description"
                label="Detailed description"
                rules={[{ required: true, message: 'Description is required' }]}
              >
                <TextArea rows={8} maxLength={16000} showCount />
              </Form.Item>
              {isKeyPersonnel && (
                <Card type="inner" title="Responsibility handover" style={{ marginBottom: 16 }}>
                  <MocHandoverFields form={form} />
                  <Button onClick={() => void runPreview()} loading={previewing} style={{ marginBottom: 12 }}>
                    Preview impact
                  </Button>
                  {preview && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {preview.warnings.map((w) => (
                        <Alert key={w} type="warning" showIcon message={w} />
                      ))}
                      <MocHandoverImpactTable
                        items={preview.items}
                        temporary={handoverReason === 'leave'}
                        editable
                        onToggle={(it, selected) =>
                          setPreview((p) =>
                            p
                              ? {
                                  ...p,
                                  items: p.items.map((x) =>
                                    x.item_type === it.item_type && x.ref_id === it.ref_id ? { ...x, selected } : x
                                  ),
                                }
                              : p
                          )
                        }
                      />
                    </Space>
                  )}
                </Card>
              )}
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={submitting}>
                    Save draft
                  </Button>
                  <Button onClick={() => navigate('/grc/moc')} disabled={submitting}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </Card>
      </Space>
    </div>
  );
};

export default MOCNewPage;
